import React, {useEffect, useMemo, useState} from 'react';
import {Form, Input, Radio, Select, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {UnmountClosed} from 'react-collapse';
import {
  StyledLegalPersonInformation,
  StyledLegalPersonInformationAddon,
  StyledLegalPersonInformationText
} from './StyledLegalPersonInformation';
import SpinSmall from '../../../../../SpinSmall';
import PhoneNumberInput from '../../../../../PhoneNumberInput';
import {StyledKYCModalContentStepTitle} from '../../../../KYCModal/StyledKYCModal';
import {EuroIcon} from '../../../../../../icons';
import {bankingActions} from '../../../../../../state/actions/banking.actions';
import {companyActions} from '../../../../../../state/actions/company.actions';
import {
  cardsHelpers,
  kycHelpers,
  localizationHelpers,
  objectHelpers,
  EU_POSTCODE_COUNTRIES,
} from '../../../../../../utils/helpers';
import {
  useCompanyName,
  useCompanySetting,
  useKYCCompany,
  useKYCCompanyHeaders,
  useLocation
} from '../../../../../../hooks';
import {bankingUserTypesConstants} from '../../../../../../constants';
import {legalPersonOptions} from './legalPersonOptions';

const gObjProp = objectHelpers.getObjProp;

const {LEGAL_PERSON} = bankingUserTypesConstants;

const {Item} = Form;

const initialFormValues = {
  email: '',
  legal_annual_turn_over: legalPersonOptions.annualTurnover[0].value,
  legal_form: undefined,
  legal_net_income_range: legalPersonOptions.income[0].value,
  legal_number_of_employee_range: legalPersonOptions.employees[0].value,
  phone: ''
}

const LegalPersonInformation = React.forwardRef(({
  onSubmit,
  ...rest
}, ref) => {
  const dispatch = useDispatch();
  const companyCountry = useCompanySetting('country');
  const companyEmail = useCompanySetting('email');
  const selectedCompanyName = useCompanyName();
  const kycCompany = useKYCCompany();
  const kycHeaders = useKYCCompanyHeaders();
  // The legal name of the company the window is for, not of whatever is selected now.
  const companyName = kycCompany?.name || selectedCompanyName;
  const [loading, setLoading] = useState(false);
  const [t] = useTranslation(['main', 'quickStart']);
  const [form] = Form.useForm();
  const [countryCode, setCountryCode] = useState(undefined);
  const [error, setError] = useState(undefined);

  const activityOutsideEu = Form.useWatch('activity_outside_eu', form);

  const isEUPostcodeCountry = EU_POSTCODE_COUNTRIES.includes(countryCode);

  const {phone_code: defaultPhoneCode} = useLocation();

  const companyTypeOptions = useMemo(() => kycHelpers.getLegalFormOptions(countryCode), [countryCode]);

  const {
    label,
    placeholder,
    validationEmailRules,
    validationPhoneRules,
    validationPostCodeRules,
    validationRequiredRules,
    title,
    tooltip
  } = useMemo(() => {
    return kycHelpers.getKYCFormDetails({t, formKey: 'lpInformation', form, countryCode})
  }, [t, form, countryCode]);

  // Everything on this step keys off the country: the company-type list comes from
  // getLegalFormOptions(countryCode), and City/Post code only render for EU postcode countries. So
  // a missing country renders a form that cannot be completed and says nothing about why — which is
  // exactly how H1MVP-4794 presented, as "Company type: No data".
  useEffect(() => {
    if (!companyCountry) {
      dispatch(companyActions.getCompanySettings());
      return;
    }

    // `getCountryCodeByName` returns undefined for a name it does not recognise, and the previous
    // `.toUpperCase()` on that threw — turning a missing lookup into a blank step with a console
    // error rather than a message.
    const resolved = localizationHelpers.getCountryCodeByName(companyCountry);
    if (!resolved) {
      setError(t('quickStart:kyc.errors.unknownCompanyCountry', {country: companyCountry}));
      return;
    }

    setCountryCode(resolved.toUpperCase());
  }, [dispatch, companyCountry, t]);

  useEffect(() => {
    if (companyEmail) form.setFieldValue('email', companyEmail);
  }, [companyEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = (fields) => {
    let data = {
      ...fields,
      country: countryCode,
      legal_name: companyName,
      legal_sector: '6201',
      user_type: LEGAL_PERSON
    };

    setLoading(true);

    dispatch(bankingActions.createUser({
      data,
      successCallback: (user) => {
        error && setError(undefined);
        setLoading(false);
        onSubmit && onSubmit({userId: gObjProp(user, 'id')});
      },
      errorCallback: (response) => {
        const formErrors = cardsHelpers.getBankingResponseErrorFields(response);
        if (formErrors.length === 0 && response?.detail?.length > 0) {
          const detail = response?.detail;
          setError(typeof detail === 'string' ? detail : detail[0].msg);
        }
        form.setFields(formErrors);
        setLoading(false);
      },
      headers: kycHeaders
    }));
  }

  return (
    <StyledLegalPersonInformation
      direction='vertical'
      size={32}
      {...rest}
    >
      <StyledKYCModalContentStepTitle>
        {title}
      </StyledKYCModalContentStepTitle>
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={onFinish}
          ref={ref}
          requiredMark={false}
        >
          <Item
            label={label('companyType')}
            name='legal_form'
            rules={validationRequiredRules}
          >
            <Select
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={companyTypeOptions}
              placeholder={placeholder('companyType')}
              size='large'
              showSearch
            />
          </Item>
          <Item
            label={label('businessEmail')}
            name='email'
            rules={validationEmailRules}
            tooltip={tooltip('businessEmail')}
          >
            <Input
              placeholder={placeholder('businessEmail')}
              size='large'
              type='email'
            />
          </Item>
          <Item
            label={label('phoneNumber')}
            name='phone'
            rules={validationPhoneRules}
          >
            <PhoneNumberInput
              phoneCode={defaultPhoneCode}
              placeholder={placeholder('phoneNumber')}
              size='large'
            />
          </Item>
          {isEUPostcodeCountry && (
            <>
              <Item
                label={label('city')}
                name='city'
                rules={validationRequiredRules}
              >
                <Input
                  placeholder={placeholder('city')}
                  size='large'
                />
              </Item>
              <Item
                label={label('postCode')}
                name='postcode'
                rules={validationPostCodeRules}
              >
                <Input
                  placeholder={placeholder('postCode')}
                  size='large'
                />
              </Item>
            </>
          )}
          <Item
            label={label('annualTurnover')}
            tooltip={tooltip('annualTurnover')}
          >
            <Space.Compact style={{width: '100%'}}>
              <StyledLegalPersonInformationAddon>
                <EuroIcon />
              </StyledLegalPersonInformationAddon>
              <Item
                name='legal_annual_turn_over'
                rules={validationRequiredRules}
                noStyle
              >
                <Select
                  options={legalPersonOptions.annualTurnover}
                  placeholder={placeholder('annualTurnover')}
                  size='large'
                />
              </Item>
            </Space.Compact>
          </Item>
          <Item
            label={label('annualIncome')}
            tooltip={tooltip('annualIncome')}
          >
            <Space.Compact style={{width: '100%'}}>
              <StyledLegalPersonInformationAddon>
                <EuroIcon />
              </StyledLegalPersonInformationAddon>
              <Item
                name='legal_net_income_range'
                rules={validationRequiredRules}
                noStyle
              >
                <Select
                  options={legalPersonOptions.income}
                  placeholder={placeholder('annualIncome')}
                  size='large'
                />
              </Item>
            </Space.Compact>
          </Item>
          <Item
            label={label('numberOfEmployees')}
            name='legal_number_of_employee_range'
            rules={validationRequiredRules}
            tooltip={tooltip('numberOfEmployees')}
          >
            <Select
              options={legalPersonOptions.employees}
              placeholder={placeholder('numberOfEmployees')}
              size='large'
            />
          </Item>
          <Item
            label={label('entitySanctionsQuestionnaire')}
            name='entity_sanctions_questionnaire'
            rules={validationRequiredRules}
          >
            <Select
              options={legalPersonOptions.entitySanctionsQuestionnaire}
              placeholder={placeholder('entitySanctionsQuestionnaire')}
              size='large'
            />
          </Item>
          <Item
            label={label('activityOutsideEu')}
            name='activity_outside_eu'
            rules={validationRequiredRules}
          >
            <Radio.Group
              options={legalPersonOptions.activityOutsideEu}
              size='large'
            />
          </Item>
          <UnmountClosed isOpened={activityOutsideEu}>
            <Item
              label={label('economicSanctions')}
              name='economic_sanctions'
              rules={validationRequiredRules}
            >
              <Radio.Group
                options={legalPersonOptions.economicSanctions}
                size='large'
              />
            </Item>
            <Item
              label={label('residentCountriesSanctions')}
              name='resident_countries_sanctions'
              rules={validationRequiredRules}
            >
              <Radio.Group
                options={legalPersonOptions.residentCountriesSanctions}
                size='large'
              />
            </Item>
            <Item
              label={label('involvedSanctions')}
              name='involved_sanctions'
              rules={validationRequiredRules}
            >
              <Radio.Group
                options={legalPersonOptions.involvedSanctions}
                size='large'
              />
            </Item>
          </UnmountClosed>

          <UnmountClosed isOpened={error !== undefined}>
            <StyledLegalPersonInformationText>
              {error}
            </StyledLegalPersonInformationText>
          </UnmountClosed>
        </Form>
      </SpinSmall>
    </StyledLegalPersonInformation>
  );
});

export default LegalPersonInformation;
